import { useEffect, useState } from 'react'
import axios from 'axios'
import { SupplierCatalogPageData } from './supplierCatalogTypes'
import { useCancellableRequest } from '../useCancellableRequest'

export const SUPPLIER_CATALOG_ROWS_PER_PAGE_OPTIONS = [25, 50, 100]

export function useSupplierCatalogSearch<T>(endpoint: string, supplier: string, initialTerm: string) {
  const defaultErrorMessage = `Failed to load the ${supplier} catalog. Please try again.`
  const [requestedTerm, setRequestedTerm] = useState('')
  const [rows, setRows] = useState<T[] | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(SUPPLIER_CATALOG_ROWS_PER_PAGE_OPTIONS[0])
  const [loading, setLoading] = useState<'' | 'loading' | 'error'>('')
  const [errorMessage, setErrorMessage] = useState(defaultErrorMessage)
  const { start, cancel: cancelRequest } = useCancellableRequest()

  useEffect(() => {
    fetchPage(initialTerm, 0, SUPPLIER_CATALOG_ROWS_PER_PAGE_OPTIONS[0])
  }, [])

  async function fetchPage(term: string, nextPage: number, nextRowsPerPage: number) {
    const { signal, isCurrent } = start()
    setLoading('loading')
    try {
      const res = await axios.get<SupplierCatalogPageData<T>>(endpoint, {
        params: { search: term, page: nextPage, pageSize: nextRowsPerPage },
        signal,
      })
      if (!isCurrent()) return
      setRequestedTerm(term)
      setRows(res.data.rows)
      setTotal(res.data.total)
      setPage(nextPage)
      setRowsPerPage(nextRowsPerPage)
      setLoading('')
    } catch (err) {
      if (!isCurrent()) return
      if (axios.isCancel(err)) return
      console.error(`Failed to search ${supplier} catalog`, err)
      const responseData = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)
        : undefined
      const serverMessage = responseData?.message
      setErrorMessage(serverMessage ?? defaultErrorMessage)
      setLoading('error')
    }
  }

  return {
    requestedTerm,
    rows,
    total,
    page,
    rowsPerPage,
    loading,
    errorMessage,
    search: (term: string) => fetchPage(term.trim(), 0, rowsPerPage),
    changePage: (nextPage: number) => fetchPage(requestedTerm, nextPage, rowsPerPage),
    changeRowsPerPage: (nextRowsPerPage: number) => fetchPage(requestedTerm, 0, nextRowsPerPage),
    cancel: () => {
      cancelRequest()
      setLoading('')
    },
    dismissError: () => setLoading(''),
  }
}
